import {
  HomeFlowDefinition,
  HomeFunnelState,
  HomeFlowType,
  HomeStepType,
  HomeFormAnswer,
} from 'scenes/home/types';
import {
  hasSpecifiedContents,
  hasBuildingCoverType,
  hasContentsCoverType,
  getPreselectedPaymentFrequency,
} from 'scenes/home/selectors/quoteSelectors';
import { YesNoAnswer } from 'scenes/common/types';
import config, { FeatureToggleState } from 'shared/config';
import { defaultSpecifiedContent } from './defaults';

export const forwardLinks: HomeFlowDefinition = {
  [HomeStepType.QuotePrice]: (funnelState: HomeFunnelState) => ({
    step: hasBuildingCoverType(funnelState)
      ? HomeStepType.BuildingExcess
      : HomeStepType.ContentsExcess,
  }),
  [HomeStepType.BuildingExcess]: (funnelState: HomeFunnelState) => ({
    step: hasContentsCoverType(funnelState)
      ? HomeStepType.ContentsExcess
      : HomeStepType.OptionalExtras,
  }),
  [HomeStepType.ContentsExcess]: {
    step: HomeStepType.OptionalExtras,
  },
  [HomeStepType.OptionalExtras]: (funnelState: HomeFunnelState) =>
    hasContentsCoverType(funnelState)
      ? {
          step: hasSpecifiedContents(funnelState)
            ? HomeStepType.SpecifiedContentList
            : HomeStepType.SpecifiedContentsAnswer,
        }
      : {
          step: HomeStepType.PropertyCondition,
          flow: HomeFlowType.PolicyDetails,
          [HomeFlowType.PolicyDetails]: {
            ...funnelState.policyDetails,
            preselectedPaymentFrequency: getPreselectedPaymentFrequency(
              funnelState
            ),
          },
        },
  [HomeStepType.SpecifiedContentsAnswer]: (
    funnelState: HomeFunnelState,
    answer: HomeFormAnswer
  ) =>
    answer.formValues.specifiedContentsAnswer === YesNoAnswer.Yes
      ? {
          step: HomeStepType.AddSpecifiedContent,
          coverOptions: {
            ...funnelState.coverOptions,
            specifiedContent: defaultSpecifiedContent,
          },
        }
      : {
          step: HomeStepType.UnspecifiedPortableContents,
        },
  [HomeStepType.AddSpecifiedContent]: {
    step: HomeStepType.SpecifiedContentList,
  },
  [HomeStepType.SpecifiedContentList]: (
    funnelState: HomeFunnelState,
    answer: HomeFormAnswer
  ) => {
    const isNewItem = answer.formValues.specifiedContentId === -1;
    const findSpecifiedContent = () =>
      funnelState.coverOptions.specifiedContents.find(
        ({ id }) => id === answer.formValues.specifiedContentId
      );

    return answer.formValues.specifiedContentsAnswer === YesNoAnswer.Yes
      ? {
          step: HomeStepType.AddSpecifiedContent,
          coverOptions: {
            ...funnelState.coverOptions,
            specifiedContent: isNewItem
              ? defaultSpecifiedContent
              : findSpecifiedContent(),
          },
        }
      : {
          step: HomeStepType.UnspecifiedPortableContents,
        };
  },
  [HomeStepType.UnspecifiedPortableContents]: (
    funnelState: HomeFunnelState
  ) => ({
    step: HomeStepType.PropertyCondition,
    flow: HomeFlowType.PolicyDetails,
    [HomeFlowType.PolicyDetails]: {
      ...funnelState.policyDetails,
      preselectedPaymentFrequency: getPreselectedPaymentFrequency(funnelState),
    },
  }),
};

export const backwardLinks: HomeFlowDefinition = {
  [HomeStepType.QuotePrice]: {
    step:
      config.featureToggles.skipHomePromoPage === FeatureToggleState.ON
        ? HomeStepType.FlybuysNumber
        : HomeStepType.PromoCode,
    flow: HomeFlowType.QuoteDetails,
  },
  [HomeStepType.BuildingExcess]: { step: HomeStepType.QuotePrice },
  [HomeStepType.ContentsExcess]: (funnelState: HomeFunnelState) => ({
    step: hasBuildingCoverType(funnelState)
      ? HomeStepType.BuildingExcess
      : HomeStepType.QuotePrice,
  }),
  [HomeStepType.OptionalExtras]: (funnelState: HomeFunnelState) => ({
    step: hasContentsCoverType(funnelState)
      ? HomeStepType.ContentsExcess
      : HomeStepType.BuildingExcess,
  }),
  [HomeStepType.SpecifiedContentsAnswer]: {
    step: HomeStepType.OptionalExtras,
  },
  [HomeStepType.AddSpecifiedContent]: (funnelState: HomeFunnelState) => ({
    step: hasSpecifiedContents(funnelState)
      ? HomeStepType.SpecifiedContentList
      : HomeStepType.SpecifiedContentsAnswer,
  }),
  [HomeStepType.SpecifiedContentList]: {
    step: HomeStepType.OptionalExtras,
  },
  [HomeStepType.UnspecifiedPortableContents]: (
    funnelState: HomeFunnelState
  ) => ({
    step: hasSpecifiedContents(funnelState)
      ? HomeStepType.SpecifiedContentList
      : HomeStepType.SpecifiedContentsAnswer,
  }),
};

export const editCoverOptions = (
  funnelState: HomeFunnelState,
  action: HomeFormAnswer
) => ({
  step: action.targetStep,
  flow: HomeFlowType.CoverOptions,
});
